/* tmc-pdp-gallery.js — the shared PDP gallery: thumbnail selection, the swipe carousel's dots and counter,
   and the lightbox. One file for the Gut, Oral, Vaginal and SIBO heroes.

   Ticket NW-45 (issue #174). Board: docs/nw/boards/pdp-gallery.md. Markup: snippets/tmc-pdp-gallery*.liquid.

   Progressive. Everything this file adds is optional: the markup is already correct on load, each
   thumbnail is a plain anchor to its own full-size image, and below 1024px the track is a CSS scroll-snap
   container that swipes without any script. This file makes a thumbnail click swap the main image, mirrors
   the carousel's position onto its dots and counter, and opens the lightbox. Nothing here depends on a
   library.

   Two layouts share one DOM. From 1024px the slides are stacked in one cell and the current one
   cross-fades in (the thumbnail row drives it). Below 1024px the slides are a horizontal scroll-snap row
   (dots and the finger drive it). The JS asks matchMedia which one it is in, so the breakpoint lives in
   one place, next to the CSS's own.

   Gut. Every tier product's images are in the page, one set each, and just the tier on the chooser is
   visible. tmc-product.js announces a tier change with a `tmc:panel-selected` event on the hero; this
   file swaps which set shows. It never reads the buy-box form, and tmc-product.js never reads this
   gallery. */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';
  var SWIPE_DISMISS_PX = 80;

  function media(query) {
    return window.matchMedia ? window.matchMedia(query).matches : false;
  }
  function reducedMotion() { return media('(prefers-reduced-motion: reduce)'); }
  function carousel() { return !media(DESKTOP); }
  function list(scope, selector) { return Array.prototype.slice.call(scope.querySelectorAll(selector)); }
  function label(n, total) { return n + ' / ' + total; }
  /* Which item of a group is current is stated once, as aria-current, so the page, the stylesheet and a
     screen reader all read the same fact. */
  function setCurrent(node, on) {
    if (on) node.setAttribute('aria-current', 'true'); else node.removeAttribute('aria-current');
  }

  function init(root) {
    if (root.dataset.galBound === '1') return;
    var dlg = root.querySelector('dialog[data-tmc-gal-lb]');
    if (!dlg) return;
    root.dataset.galBound = '1';
    root.classList.add('is-enhanced');
    list(root, '[data-tmc-gal-magnify]').forEach(function (btn) { btn.hidden = false; });

    var hero = root.closest('[data-tmc-product-hero]') || root;
    var view = dlg.querySelector('[data-tmc-gal-lb-view]');
    var strip = dlg.querySelector('[data-tmc-gal-lb-strip]');
    var lbDots = dlg.querySelector('[data-tmc-gal-lb-dots]');
    var lbCount = dlg.querySelector('[data-tmc-gal-lb-count]');
    var lbTitle = dlg.querySelector('[data-tmc-gal-lb-title]');
    var closeBtn = dlg.querySelector('[data-tmc-gal-lb-close]');
    var prevBtn = dlg.querySelector('[data-tmc-gal-lb-prev]');
    var nextBtn = dlg.querySelector('[data-tmc-gal-lb-next]');

    var lb = { open: false, set: null, index: 1, total: 0, opener: null, settle: 0, lock: false };

    /* ---------- the gallery ---------- */

    function activeSet() { return root.querySelector('[data-tmc-gal-set]:not([hidden])'); }
    function slidesOf(set) { return set ? list(set, '[data-tmc-gal-slide]') : []; }
    function thumbsOf(set) { return set ? list(set, 'a[data-tmc-gal-thumb]') : []; }
    function currentIndex(set) {
      var slides = slidesOf(set);
      for (var i = 0; i < slides.length; i++) if (slides[i].getAttribute('aria-current') === 'true') return i + 1;
      return 1;
    }
    function scrollTrack(track, index, instant) {
      if (!track || typeof track.scrollTo !== 'function') return;
      var w = track.clientWidth || 0;
      track.scrollTo({ left: w * (index - 1), behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
    }

    /* Make image `index` (1-based) the current one in `set`: the slide, the thumbnail, the dot, the counter.
       `scroll` also moves the carousel track, for a dot, a thumbnail or the lightbox handing back an index;
       a scroll the shopper is already doing must not fight itself, so the scroll handler passes false. */
    function select(set, index, scroll, instant) {
      var slides = slidesOf(set);
      if (!slides.length) return;
      index = Math.max(1, Math.min(slides.length, index));
      slides.forEach(function (slide, i) { setCurrent(slide, i + 1 === index); });
      thumbsOf(set).forEach(function (thumb, i) { setCurrent(thumb, i + 1 === index); });
      list(set, '[data-tmc-gal-dot]').forEach(function (dot, i) { setCurrent(dot, i + 1 === index); });
      var count = set.querySelector('[data-tmc-gal-count]');
      if (count) count.textContent = label(index, slides.length);
      if (scroll && carousel()) scrollTrack(set.querySelector('[data-tmc-gal-track]'), index, instant);
    }

    function showSet(panelId) {
      var sets = list(root, '[data-tmc-gal-set]');
      var next = null;
      sets.forEach(function (set) { if (set.dataset.panelId === String(panelId)) next = set; });
      if (!next) return;
      sets.forEach(function (set) { set.hidden = set !== next; });
      select(next, 1, true, true);
    }
    hero.addEventListener('tmc:panel-selected', function (event) {
      if (event.detail) showSet(event.detail.panelId);
    });

    /* Thumbnails: a click commits, hover is a CSS cue and nothing more, and the arrow keys move focus along the row
       without selecting (focus is orange so it never reads as selection). */
    root.addEventListener('click', function (event) {
      var thumb = event.target.closest('a[data-tmc-gal-thumb]');
      if (thumb && root.contains(thumb)) {
        event.preventDefault();
        select(thumb.closest('[data-tmc-gal-set]'), parseInt(thumb.dataset.index, 10), true);
        return;
      }
      var dot = event.target.closest('[data-tmc-gal-dot]');
      if (dot) {
        select(dot.closest('[data-tmc-gal-set]'), parseInt(dot.dataset.index, 10), true);
        lockScroll();
        return;
      }
      var open = event.target.closest('a[data-tmc-gal-open]');
      if (open && root.contains(open)) {
        /* Leave a modified click to the browser: open-in-new-tab is the anchor doing its job. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        openLightbox(open);
        return;
      }
      var magnify = event.target.closest('[data-tmc-gal-magnify]');
      if (magnify) {
        var set = magnify.closest('[data-tmc-gal-set]');
        var slide = slidesOf(set)[currentIndex(set) - 1];
        openLightbox(slide ? slide.querySelector('a[data-tmc-gal-open]') : magnify);
      }
    });
    root.addEventListener('keydown', function (event) {
      var thumb = event.target.closest && event.target.closest('a[data-tmc-gal-thumb]');
      if (!thumb) return;
      var thumbs = thumbsOf(thumb.closest('[data-tmc-gal-set]'));
      var at = thumbs.indexOf(thumb);
      var to = -1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') to = Math.min(thumbs.length - 1, at + 1);
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') to = Math.max(0, at - 1);
      else if (event.key === 'Home') to = 0;
      else if (event.key === 'End') to = thumbs.length - 1;
      if (to < 0) return;
      event.preventDefault();
      thumbs[to].focus();
    });

    /* The carousel: the current image is whichever the track has scrolled to. A dot or the lightbox moves
       the track itself; while that scroll runs its own scroll events must not re-select the images it
       passes through, so they are ignored until the track has been still for a beat. */
    var settleTimer = 0;
    var locked = false;
    function lockScroll() {
      locked = true;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(function () { locked = false; }, 400);
    }
    list(root, '[data-tmc-gal-track]').forEach(function (track) {
      track.addEventListener('scroll', function () {
        if (locked || !carousel()) return;
        var w = track.clientWidth;
        if (!w) return;
        select(track.closest('[data-tmc-gal-set]'), Math.round(track.scrollLeft / w) + 1, false);
      }, { passive: true });
    });

    /* Crossing the breakpoint: entering the carousel, put the track back on the current image. */
    if (window.matchMedia) {
      var mq = window.matchMedia(DESKTOP);
      var onChange = function () {
        var set = activeSet();
        if (set && carousel()) scrollTrack(set.querySelector('[data-tmc-gal-track]'), currentIndex(set), true);
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }

    /* ---------- the lightbox ---------- */

    function el(tag, className, attrs) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (attrs) Object.keys(attrs).forEach(function (k) { node.setAttribute(k, attrs[k]); });
      return node;
    }
    function lbSlides() { return list(view, '[data-tmc-gal-lb-slide]'); }

    /* Built when it opens, from the anchors already in the page. The larger variant is the anchor's own
       href, so it is requested here and never before (build note 03). */
    function build(set, startIndex) {
      view.textContent = '';
      strip.textContent = '';
      lbDots.textContent = '';
      var slides = slidesOf(set);
      var thumbs = thumbsOf(set);
      slides.forEach(function (slide, i) {
        var link = slide.querySelector('a[data-tmc-gal-open]');
        var small = slide.querySelector('img');
        var item = el('div', 'tmc-gal-lb__slide', { 'data-tmc-gal-lb-slide': '', 'data-index': String(i + 1) });
        var big = el('img', 'tmc-gal-lb__img');
        big.setAttribute('src', link.getAttribute('href'));
        big.setAttribute('alt', small ? small.getAttribute('alt') || '' : '');
        big.setAttribute('width', link.dataset.w || '');
        big.setAttribute('height', link.dataset.h || '');
        big.setAttribute('decoding', 'async');
        big.setAttribute('loading', i + 1 === startIndex ? 'eager' : 'lazy');
        item.appendChild(big);
        view.appendChild(item);

        var slot = el('li', 'tmc-gal-lb__strip-slot');
        var pick = el('button', 'tmc-gal-lb__thumb', { type: 'button', 'data-index': String(i + 1), 'aria-label': 'Show image ' + (i + 1) + ' of ' + slides.length });
        var thumbImg = thumbs[i] && thumbs[i].querySelector('img');
        var tile = el('span', 'tmc-gal-lb__tile');
        if (thumbImg) {
          var copy = thumbImg.cloneNode(false);
          copy.removeAttribute('loading');
          tile.appendChild(copy);
        }
        pick.appendChild(tile);
        slot.appendChild(pick);
        strip.appendChild(slot);

        lbDots.appendChild(el('button', 'tmc-gal-lb__dot', { type: 'button', 'data-index': String(i + 1), 'aria-label': 'Show image ' + (i + 1) + ' of ' + slides.length }));
      });
      lbTitle.textContent = set.dataset.title || '';
    }

    function paint() {
      lbSlides().forEach(function (item, i) { setCurrent(item, i + 1 === lb.index); });
      list(strip, 'button').forEach(function (b, i) { setCurrent(b, i + 1 === lb.index); });
      list(lbDots, 'button').forEach(function (b, i) { setCurrent(b, i + 1 === lb.index); });
      /* The product's name leads the counter on desktop ("GUT MICROBIOME MAX · 3 / 5"); on touch the title
         sits under the pager instead, so the name is its own span for the stylesheet to hide. */
      var name = lbTitle.textContent;
      lbCount.textContent = '';
      if (name) {
        var lead = el('span', 'tmc-gal-lb__name');
        lead.textContent = name + ' · ';
        lbCount.appendChild(lead);
      }
      lbCount.appendChild(document.createTextNode(label(lb.index, lb.total)));
      prevBtn.setAttribute('aria-disabled', lb.index <= 1 ? 'true' : 'false');
      nextBtn.setAttribute('aria-disabled', lb.index >= lb.total ? 'true' : 'false');
      dlg.dataset.single = lb.total < 2 ? 'true' : 'false';
    }

    function go(index, scroll) {
      var next = Math.max(1, Math.min(lb.total, index));
      if (next === lb.index) return;
      lb.index = next;
      paint();
      if (scroll && carousel()) scrollView(next);
    }
    function scrollView(index) {
      lb.lock = true;
      window.clearTimeout(lb.settle);
      lb.settle = window.setTimeout(function () { lb.lock = false; }, 400);
      if (typeof view.scrollTo !== 'function') return;
      var target = lbSlides()[index - 1];
      if (!target) return;
      /* Centre the slide: its own midpoint against the viewer's. */
      var left = target.offsetLeft - (view.clientWidth - target.clientWidth) / 2;
      view.scrollTo({ left: left, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    function openLightbox(from) {
      var set = from && from.closest ? from.closest('[data-tmc-gal-set]') : null;
      set = set || activeSet();
      var slides = slidesOf(set);
      if (!slides.length) return;
      lb.set = set;
      lb.total = slides.length;
      lb.index = currentIndex(set);
      lb.opener = from && from.focus ? from : null;
      lb.open = true;
      build(set, lb.index);
      paint();
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
      if (carousel()) {
        /* Layout exists once the dialog is open, and not before it, so centre the current slide after showing it. */
        var inst = lbSlides()[lb.index - 1];
        if (inst && typeof view.scrollTo === 'function') view.scrollTo({ left: inst.offsetLeft - (view.clientWidth - inst.clientWidth) / 2, behavior: 'auto' });
      }
      closeBtn.focus();
    }

    /* Closing hands the gallery where the shopper ended: the thumbnail and the main image both follow, and
       focus goes back to the image they clicked. Native Esc and the close() call both land here, once. */
    function onClosed() {
      if (!lb.open) return;
      lb.open = false;
      select(lb.set, lb.index, true, true);
      var slide = slidesOf(lb.set)[lb.index - 1];
      var back = slide && slide.querySelector('a[data-tmc-gal-open]');
      if (back && back.focus) back.focus();
      else if (lb.opener && lb.opener.focus) lb.opener.focus();
      lb.opener = null;
    }
    function closeLightbox() {
      if (!lb.open) return;
      if (typeof dlg.close === 'function' && dlg.hasAttribute('open')) dlg.close();
      else dlg.removeAttribute('open');
      onClosed();
    }
    dlg.addEventListener('close', onClosed);

    closeBtn.addEventListener('click', closeLightbox);
    prevBtn.addEventListener('click', function () { go(lb.index - 1, true); });
    nextBtn.addEventListener('click', function () { go(lb.index + 1, true); });
    strip.addEventListener('click', function (event) {
      var b = event.target.closest('button[data-index]');
      if (b) go(parseInt(b.dataset.index, 10), true);
    });
    lbDots.addEventListener('click', function (event) {
      var b = event.target.closest('button[data-index]');
      if (b) go(parseInt(b.dataset.index, 10), true);
    });

    /* A click on the scrim closes it. The scrim is the dialog's own backdrop (target is the dialog) or the
       empty part of the viewer around the picture; a click on the picture itself does nothing. */
    dlg.addEventListener('click', function (event) {
      var t = event.target;
      if (t === dlg || t === view || (t.hasAttribute && t.hasAttribute('data-tmc-gal-lb-slide'))) closeLightbox();
    });
    dlg.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { event.preventDefault(); closeLightbox(); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); go(lb.index + 1, true); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); go(lb.index - 1, true); }
    });

    /* Touch: the viewer's row is a scroll-snap track, so paging is native; this just mirrors the position
       and dismisses on a swipe down. */
    view.addEventListener('scroll', function () {
      if (lb.lock || !carousel() || !lb.open) return;
      var slides = lbSlides();
      if (!slides.length) return;
      var mid = view.scrollLeft + view.clientWidth / 2;
      var nearest = 1;
      var gap = Infinity;
      slides.forEach(function (s, i) {
        var d = Math.abs(s.offsetLeft + s.clientWidth / 2 - mid);
        if (d < gap) { gap = d; nearest = i + 1; }
      });
      go(nearest, false);
    }, { passive: true });
    var touchStart = null;
    dlg.addEventListener('touchstart', function (event) {
      var t = event.touches && event.touches[0];
      touchStart = t ? { x: t.clientX, y: t.clientY } : null;
    }, { passive: true });
    dlg.addEventListener('touchend', function (event) {
      var t = event.changedTouches && event.changedTouches[0];
      if (!touchStart || !t) return;
      var dx = t.clientX - touchStart.x;
      var dy = t.clientY - touchStart.y;
      touchStart = null;
      if (dy > SWIPE_DISMISS_PX && Math.abs(dy) > Math.abs(dx) * 1.5) closeLightbox();
    }, { passive: true });
  }

  function boot(scope) {
    list(scope || document, '[data-tmc-gal]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})();
